import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Customer } from '../types/partner';
import { telstraPartnerService, TelstraMobileReport } from '../services/telstraService';
import { parseTelstraMobileXlsxFile } from '../utils/telstraMobileParser';
import { useAuth } from '../context/AuthContext';
import { 
  DeviceTypePieChart, 
  TopCostBarChart, 
  DataUsageChart, 
  PlanDistributionChart, 
  QuickStats 
} from '../components/telstra/TelstraCharts';
import { TableSkeleton } from '../components/ui/Skeleton';
import { formatCurrency } from '../utils/telstraUtils';
import { summariseInactive, hasBurst, inactivityTier } from '../lib/telstraInsights';
import { Phone, FileText, Calendar, Award, Check, X } from 'lucide-react';
import { TelstraBlitzValidationModal } from '../components/TelstraBlitzValidationModal';
import { TelstraInvoicesPanel } from '../components/telstra/TelstraInvoicesPanel';
import { isZeroBillReport } from '../utils/telstraBlitzValidation';
import { useCustomerFeatures } from '../hooks/useCustomerFeatures';

type ViewMode = 'current' | 'history' | 'comparison' | 'analytics' | 'invoices';

interface TelstraProps {
  customer?: Customer;
}

export default function Telstra({ customer: selectedCustomer }: TelstraProps) {
  const { partner } = useAuth();
  const { features: customerFeatures } = useCustomerFeatures(selectedCustomer?.customerId);
  const invoiceAccess = customerFeatures.telstraInvoices === true;
  const [report, setReport] = useState<TelstraMobileReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mobilePreview, setMobilePreview] = useState<{ report: TelstraMobileReport; warnings: string[]; originalFile: File } | null>(null);
  const [months, setMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'info'|'success'|'warning'|'error' }>({ show: false, message: '', type: 'info' });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // New state for enhanced features
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [searchQuery, setSearchQuery] = useState('');
  const [comparisonMonths, setComparisonMonths] = useState<string[]>([]);
  const [comparisonReports, setComparisonReports] = useState<{ month: string; report: TelstraMobileReport }[]>([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  
  // Customer confirmation modal state (mobile reports). The modal owns the
  // confirmation checkbox and statement-month override internally.
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!invoiceAccess && viewMode === 'invoices') setViewMode('current');
  }, [invoiceAccess, viewMode]);
  // Column definitions for Mobile Services table
  const allColumns = useMemo(() => [
    { key: 'mobileNumber', label: 'Mobile Number', default: true },
    { key: 'userName', label: 'User Name', default: true },
    { key: 'planName', label: 'Plan', default: true },
    { key: 'monthlyFee', label: 'Monthly Fee', default: true },
    { key: 'latestMonthBillTotal', label: 'Bill Total', default: true },
    { key: 'latestMonthDataMb', label: 'Data Used', default: true },
    { key: 'includedData', label: 'Data Allowance', default: false },
    { key: 'serviceActivationDate', label: 'Activation Date', default: false },
    { key: 'deviceType', label: 'Device Type', default: true },
    { key: 'deviceModel', label: 'Device Model', default: false },
    { key: 'deviceMake', label: 'Device Make', default: false },
    { key: 'deviceModelNumber', label: 'Model #', default: false },
    { key: 'deviceOperatingSystem', label: 'Device OS', default: false },
    { key: 'deviceCategory', label: 'Device Category', default: false },
    { key: 'deviceAgeMonths', label: 'Device Age', default: false },
    { key: 'accountNumber', label: 'Account #', default: false },
    { key: 'simNumber', label: 'SIM #', default: false },
    { key: 'deviceImei', label: 'IMEI', default: false },
    { key: 'irActive', label: 'Roaming Enabled', default: false },
    { key: 'barRoaming', label: 'Roaming Bar', default: false },
    { key: 'barIdd', label: 'IDD Bar', default: false },
    { key: 'latestMonthVoiceCalls', label: 'Voice Calls', default: false },
    { key: 'latestMonthVoiceMinutes', label: 'Voice Minutes', default: false },
    { key: 'latestMonthSms', label: 'SMS', default: false },
    { key: 'avg3mIrDataMb', label: 'Avg IR Data (3m)', default: false },
    { key: 'avg3mIddExcess', label: 'Avg IDD Excess (3m)', default: false },
    { key: 'avg3mDataMb', label: 'Avg Data (3m)', default: false },
    { key: 'avg3mVoiceMinutes', label: 'Avg Voice (3m)', default: false },
    { key: 'avg3mBill', label: 'Avg Bill (3m)', default: false },
    { key: 'deviceTradeInValue', label: 'Trade-In Value', default: false },
    { key: 'inactivity', label: 'Potentially Inactive', default: false },
    { key: 'usageBurst', label: 'Usage Spike', default: false }
  ], []);

  // Load saved column preferences from localStorage
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('telstra-visible-columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return allColumns.filter(c => c.default).map(c => c.key);
      }
    }
    return allColumns.filter(c => c.default).map(c => c.key);
  });

  // Save column preferences to localStorage
  const toggleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const updated = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key];
      localStorage.setItem('telstra-visible-columns', JSON.stringify(updated));
      return updated;
    });
  };
  const showToast = (message: string, type: 'info'|'success'|'warning'|'error' = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 2500);
  };

  useEffect(() => {
    const load = async () => {
      if (!selectedCustomer) return;
      setLoading(true);
      setError(null);
      try {
        const r = await telstraPartnerService.getLatestMobileReport(selectedCustomer.customerId);
        setReport(r);
        setSelectedMonth(r.statementMonth);
      } catch (e: any) {
        if (e && typeof e === 'object' && 'status' in e && (e as any).status === 404) {
          setReport(null);
          setError(null);
        } else {
          setError(e?.message || 'Failed to load Telstra report');
        }
      } finally {
        setLoading(false);
      }
      try {
        const listed = await telstraPartnerService.listRecentMobileReportMonths(selectedCustomer.customerId, 6);
        setMonths(listed.months || []);
      } catch {
        setMonths([]);
      }
    };
    load();
  }, [selectedCustomer]);

  const fetchMonth = async (month: string) => {
    if (!selectedCustomer) return;
    try {
      setLoading(true);
      const res = await telstraPartnerService.getMobileReportByMonth(selectedCustomer.customerId, month);
      setReport(res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const rows = useMemo(() => mobilePreview?.report.rows || report?.rows || [], [mobilePreview, report]);

  const kpis = useMemo(() => {
    const activeReport = mobilePreview?.report || report;
    return {
      lines: activeReport?.totals.lines || 0,
      monthlyFees: activeReport?.totals.totalMonthlyFees || 0,
      latestBills: activeReport?.totals.totalLatestMonthBills || 0,
      dataMb: activeReport?.totals.totalLatestMonthDataMb || 0,
      dataAllowanceMb: activeReport?.totals.totalIncludedDataMb || 0,
      statementMonth: activeReport?.statementMonth || '',
    };
  }, [mobilePreview, report]);

  const inactiveSummary = useMemo(() => summariseInactive(rows), [rows]);
  const burstCount = useMemo(() => rows.filter(r => hasBurst(r)).length, [rows]);

// Enhanced filtering
  const filteredAndSortedRows = useMemo(() => {
    let result = [...rows];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row => 
        row.mobileNumber.toLowerCase().includes(query) ||
        row.userName.toLowerCase().includes(query) ||
        row.accountNumber.toLowerCase().includes(query) ||
        row.planName.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [rows, searchQuery]);

  // Export to CSV functionality
  const exportToCSV = () => {
    if (!report && !mobilePreview) {
      showToast('No data to export', 'warning');
      return;
    }
    
    const dataToExport = mobilePreview ? mobilePreview.report.rows : rows;
    const headers = ['Account Number', 'Mobile Number', 'User Name', 'Plan Name', 'Monthly Fee', 
                     'Included Data', 'SIM Number', 'Device IMEI', 'Device Model', 'Device Type',
                     'Service Activation Date', 'Device Make', 'Device Model Number', 'Device OS', 'Device Category', 'Device Age Months',
                     'Latest Month Data MB', 'Latest Month Voice Calls', 'Latest Month Voice Minutes',
                     'Latest Month SMS', 'Latest Month Bill Total', 'Avg 3M Data MB', 
                     'Avg 3M Voice Minutes', 'Avg 3M Bill', 'Device Trade-In Value',
                     'Roaming Enabled', 'Roaming Barred', 'IDD Barred', 'No Usage 3M', 'No Usage 6M',
                     'No Network Activity 3M', 'No Network Activity 6M', 'Only Diversion 3M', 'Only Diversion 6M'];
    
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => [
        row.accountNumber,
        row.mobileNumber,
        row.userName,
        row.planName,
        row.monthlyFee,
        row.includedData,
        row.simNumber,
        row.deviceImei || '',
        row.deviceModel || '',
        row.deviceType || '',
        row.serviceActivationDate || '',
        row.deviceMake || '',
        row.deviceModelNumber || '',
        row.deviceOperatingSystem || '',
        row.deviceCategory || '',
        row.deviceAgeMonths || '',
        row.latestMonthDataMb,
        row.latestMonthVoiceCalls,
        row.latestMonthVoiceMinutes,
        row.latestMonthSms,
        row.latestMonthBillTotal,
        row.avg3mDataMb,
        row.avg3mVoiceMinutes,
        row.avg3mBill,
        row.deviceTradeInValue || '',
        row.irActive || '',
        row.barRoaming || '',
        row.barIdd || '',
        row.noUsage3m || '',
        row.noUsage6m || '',
        row.noNetworkActivity3m || '',
        row.noNetworkActivity6m || '',
        row.onlyDiversionUsage3m || '',
        row.onlyDiversionUsage6m || ''
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `telstra-blitz-${kpis.statementMonth || 'report'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Export successful', 'success');
  };

  // Load comparison data
  const loadComparisonData = async () => {
    if (!selectedCustomer || comparisonMonths.length < 2) return;
    
    setLoading(true);
    setComparisonReports([]);
    try {
      const reports = await Promise.all(
        comparisonMonths.map(async (month) => {
          const report = await telstraPartnerService.getMobileReportByMonth(selectedCustomer.customerId, month);
          return { month, report };
        })
      );
      // Sort by month descending (newest first)
      reports.sort((a, b) => b.month.localeCompare(a.month));
      setComparisonReports(reports);
      showToast('Comparison data loaded', 'success');
    } catch (e: any) {
      setError(e?.message || 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve and save after customer confirmation. Called from the
  // shared TelstraBlitzValidationModal with the (possibly month-overridden)
  // report ready to upload.
  const handleApproveAndSave = async (reportToSave: TelstraMobileReport) => {
    if (!selectedCustomer) return;

    try {
      if (isZeroBillReport(reportToSave)) {
        const month = reportToSave.statementMonth || 'this month';
        const proceed = window.confirm(
          `Warning: Latest Month Bill totals are $0 for ${month}. This usually means the bill hasn't been generated yet.\n\nIt's recommended to review the report and avoid approving/sending it to the customer yet.\n\nDo you still want to approve & save?`
        );
        if (!proceed) {
          return;
        }
      }
      setUploading(true);
      const pid = partner?.partnerId || 'self';
      const res = await telstraPartnerService.uploadMobileReport(pid, selectedCustomer.customerId, reportToSave, mobilePreview?.originalFile);
      if (!res?.reportId) throw new Error('Failed to save report');
      setMobilePreview(null);
      setShowConfirmModal(false);
      const latest = await telstraPartnerService.getLatestMobileReport(selectedCustomer.customerId);
      setReport(latest);
      setSelectedMonth(latest.statementMonth);
      const listed = await telstraPartnerService.listRecentMobileReportMonths(selectedCustomer.customerId, 6);
      setMonths(listed.months || []);
      showToast(`Saved ${latest.statementMonth}`, 'success');
    } catch (err: any) {
      setError(err?.message || 'Failed to save report');
      showToast('Failed to save report', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Phone className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Telstra Bill Management</h2>
              <p className="text-sm text-slate-500">
                {kpis.statementMonth || 'No report loaded'} • {kpis.lines} mobile lines
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mobilePreview && (
              <Badge className="bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20">
                Preview: {mobilePreview.report.statementMonth}
              </Badge>
            )}
            {months.length > 0 && !mobilePreview && (
              <select
                title="Select statement month"
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
                value={selectedMonth}
                onChange={async (e) => {
                  const m = e.target.value;
                  setSelectedMonth(m);
                  await fetchMonth(m);
                }}
              >
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              aria-hidden="true"
              title="Upload Telstra XLSX"
              onChange={async (e) => {
                try {
                  if (!selectedCustomer || !e.target.files || e.target.files.length === 0) return;
                  setUploading(true);
                  setError(null);
                  const file = e.target.files[0];
                  const preview = await parseTelstraMobileXlsxFile(file, selectedCustomer.customerId, { sheetName: 'Blitz Summary', sheetIndex: 1 });
                  const additionalWarnings: string[] = [];
                  if (isZeroBillReport(preview.report)) {
                    const month = preview.report.statementMonth || 'this month';
                    window.alert(`Heads up: Latest Month Bill totals are $0 for ${month}. This usually means the Telstra bill hasn't been generated yet. Please review the report and probably don't approve/send it to the customer yet.`);
                    additionalWarnings.push('All Latest Month Bill totals are $0. This likely means the bill has not been generated yet. Review the report and consider not approving/sending it to the customer yet.');
                  }
                  setMobilePreview({ report: preview.report, warnings: [...preview.warnings, ...additionalWarnings], originalFile: file });
                  showToast(`Preview loaded: ${preview.report.rows.length} lines`, 'info');
                } catch (err: any) {
                  setError(err?.message || 'Failed to parse XLSX');
                  showToast(err?.message || 'Failed to parse XLSX', 'error');
                } finally {
                  setUploading(false);
                  if (e.target) e.target.value = '';
                }
              }}
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload XLSX
            </Button>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {mobilePreview && (
              <>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  disabled={uploading}
                  onClick={() => setShowConfirmModal(true)}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve & Save
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setMobilePreview(null);
                    showToast('Preview cancelled', 'info');
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel Preview
                </Button>
              </>
            )}
            
            {!mobilePreview && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportToCSV}
                  disabled={!report && !mobilePreview}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={!selectedCustomer || uploading || !selectedMonth}
                  onClick={async () => {
                    if (!selectedCustomer || !selectedMonth) return;
                    const confirmDelete = window.confirm(`Delete report for ${selectedMonth}?`);
                    if (!confirmDelete) return;
                    try {
                      setUploading(true);
                      await telstraPartnerService.deleteMobileReportByMonth(selectedCustomer.customerId, selectedMonth);
                      const listed = await telstraPartnerService.listRecentMobileReportMonths(selectedCustomer.customerId, 6);
                      setMonths(listed.months || []);
                      if (listed.months && listed.months.length > 0) {
                        setSelectedMonth(listed.months[0]);
                        await fetchMonth(listed.months[0]);
                      } else {
                        setReport(null);
                        setSelectedMonth('');
                      }
                      showToast(`Deleted ${selectedMonth}`, 'success');
                    } catch (err: any) {
                      showToast(err?.message || 'Failed to delete report', 'error');
                    } finally {
                      setUploading(false);
                    }
                  }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6" />
                  </svg>
                  Delete
                </Button>
              </>
            )}
          </div>
          
          {viewMode === 'current' && (
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
                <svg className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchQuery && (
                <Badge className="bg-orange-50 text-orange-700 ring-1 ring-orange-600/20">
                  {filteredAndSortedRows.length} results
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Mode Navigation Tabs */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6">
        <nav className="flex gap-1 -mb-px">
          {[
            { key: 'current', label: 'Current Report', icon: FileText },
            { key: 'history', label: 'History', icon: Calendar },
            { key: 'comparison', label: 'Compare', icon: Award },
            { key: 'analytics', label: 'Analytics', icon: Award },
            ...(invoiceAccess ? [{ key: 'invoices', label: 'Invoices', icon: FileText }] : []),
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setViewMode(key as ViewMode)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                viewMode === key
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <X className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
        
        {mobilePreview && mobilePreview.warnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <p className="font-semibold text-yellow-800 mb-2">Preview Warnings:</p>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
              {mobilePreview.warnings.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {viewMode === 'current' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">{kpis.lines}</div>
                    <div className="text-xs text-slate-500">Mobile Lines</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">${kpis.monthlyFees.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">Monthly Fees</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">${kpis.latestBills.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">Latest Month Total</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-slate-900">
                      {(kpis.dataMb / 1024).toFixed(1)} GB
                    </div>
                    <div className="text-xs text-slate-500">
                      Data Used{kpis.dataAllowanceMb > 0 ? ` / ${(kpis.dataAllowanceMb / 1024).toFixed(1)} GB` : ''}
                    </div>
                    {kpis.dataAllowanceMb > 0 && (
                      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 rounded-full"
                          style={{ width: `${Math.min(100, (kpis.dataMb / kpis.dataAllowanceMb) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Blitz Insights */}
            {(inactiveSummary.inactive6m.length > 0 || inactiveSummary.inactive3mOnly.length > 0 || burstCount > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Dormant 6+ months</div>
                    <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">NO ACTIVITY</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{inactiveSummary.inactive6m.length}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Est. <span className="font-semibold">{formatCurrency(inactiveSummary.annualSavings6m)}/yr</span> saveable
                  </div>
                  <div className="text-[10.5px] text-slate-400 mt-1 leading-tight">
                    Raise with customer. Verify contract terms before disconnecting.
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">Dormant 3 months</div>
                    <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">REVIEW</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{inactiveSummary.inactive3mOnly.length}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Est. <span className="font-semibold">{formatCurrency(inactiveSummary.annualSavings3m)}/yr</span> if disconnected
                  </div>
                  <div className="text-[10.5px] text-slate-400 mt-1 leading-tight">
                    May return to use. Confirm with line owner.
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-orange-200 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-orange-700">Usage spike this month</div>
                    <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">SPIKE ↑</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{burstCount}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Latest month cost or data ≥1.5× 11-month average.
                  </div>
                  <div className="text-[10.5px] text-slate-400 mt-1 leading-tight">
                    Add Potentially Inactive + Usage Spike columns to drill down.
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Services Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Mobile Services</h3>
                <div className="relative">
                  <button
                    onClick={() => setShowColumnPicker(!showColumnPicker)}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Columns ({visibleColumns.length}/{allColumns.length})
                  </button>
                  {showColumnPicker && (
                    <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                      <div className="p-3 border-b border-slate-100">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-slate-700">Visible Columns</span>
                          <button 
                            onClick={() => {
                              const defaultCols = allColumns.filter(c => c.default).map(c => c.key);
                              setVisibleColumns(defaultCols);
                              localStorage.setItem('telstra-visible-columns', JSON.stringify(defaultCols));
                            }}
                            className="text-xs text-orange-600 hover:text-orange-700"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="p-2">
                        {allColumns.map(col => (
                          <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.key)}
                              onChange={() => toggleColumn(col.key)}
                              className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-slate-700">{col.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Click outside to close column picker */}
              {showColumnPicker && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowColumnPicker(false)}
                />
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      {allColumns.filter(col => visibleColumns.includes(col.key)).map(col => (
                        <th key={col.key} className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {loading ? (
                      <TableSkeleton rows={10} columns={visibleColumns.length} />
                    ) : filteredAndSortedRows.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                              <FileText className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">No data found</h3>
                            <p className="text-sm text-slate-500">
                              {searchQuery ? 'Try adjusting your search' : 'Upload a report to see data'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedRows.slice(0, 2000).map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                          {allColumns.filter(col => visibleColumns.includes(col.key)).map(col => {
                            if (col.key === 'inactivity') {
                              const tier = inactivityTier(row);
                              return (
                                <td key={col.key} className="px-6 py-3 text-sm whitespace-nowrap">
                                  {tier === '6m' ? (
                                    <span className="inline-flex items-center text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Dormant 6m+</span>
                                  ) : tier === '3m' ? (
                                    <span className="inline-flex items-center text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Dormant 3m</span>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              );
                            }
                            if (col.key === 'usageBurst') {
                              const spike = hasBurst(row);
                              return (
                                <td key={col.key} className="px-6 py-3 text-sm whitespace-nowrap">
                                  {spike ? (
                                    <span className="inline-flex items-center text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">Spike ↑</span>
                                  ) : (
                                    <span className="text-slate-300">-</span>
                                  )}
                                </td>
                              );
                            }
                            const val = (row as any)[col.key];
                            // Format specific columns
                            let displayVal = val;
                            if (col.key === 'monthlyFee' || col.key === 'latestMonthBillTotal' || col.key === 'avg3mBill' || col.key === 'deviceTradeInValue' || col.key === 'avg3mIddExcess') {
                              displayVal = val != null && Number(val) > 0 ? `$${Number(val).toFixed(2)}` : '-';
                            } else if (col.key === 'latestMonthDataMb' || col.key === 'includedData' || col.key === 'avg3mDataMb' || col.key === 'avg3mIrDataMb') {
                              displayVal = val != null && Number(val) > 0 ? `${Math.round(Number(val) / 1024 * 10) / 10} GB` : '-';
                            } else if (col.key === 'deviceAgeMonths') {
                              displayVal = val != null && Number(val) > 0 ? `${Number(val).toFixed(1)} mo` : '-';
                            } else if (col.key === 'latestMonthVoiceMinutes' || col.key === 'avg3mVoiceMinutes') {
                              displayVal = val != null && Number(val) > 0 ? `${Math.round(Number(val))} min` : '-';
                            } else if (val == null || val === '') {
                              displayVal = '-';
                            }
                            return (
                              <td key={col.key} className="px-6 py-3 text-sm text-slate-700 whitespace-nowrap">
                                {displayVal}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {filteredAndSortedRows.length > 2000 && (
                <div className="p-3 text-center text-sm text-slate-500 bg-slate-50 border-t border-slate-200">
                  Showing 2,000 of {filteredAndSortedRows.length.toLocaleString()} rows
                </div>
              )}
            </div>
          </>
        )}

        {viewMode === 'history' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Report History</h3>
              </div>
              {months.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Report History</h3>
                  <p className="text-sm text-slate-500">Upload your first Telstra report to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statement Month</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Lines</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {months.map((month) => (
                        <tr key={month} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{month}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            <Badge className="bg-blue-50 text-blue-700 ring-1 ring-blue-600/20">Mobile</Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            {month === selectedMonth && report ? report.rows.length : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  setSelectedMonth(month);
                                  await fetchMonth(month);
                                  setViewMode('current');
                                }}
                                className="text-orange-600 hover:text-orange-800 font-medium"
                              >
                                View
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Delete report for ${month}?`)) {
                                    try {
                                      setUploading(true);
                                      await telstraPartnerService.deleteMobileReportByMonth(selectedCustomer!.customerId, month);
                                      const listed = await telstraPartnerService.listRecentMobileReportMonths(selectedCustomer!.customerId, 6);
                                      setMonths(listed.months || []);
                                      showToast(`Deleted ${month}`, 'success');
                                    } catch (err: any) {
                                      showToast(err?.message || 'Failed to delete', 'error');
                                    } finally {
                                      setUploading(false);
                                    }
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'comparison' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900">Compare Reports</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-4">Select up to 3 months to compare side by side:</p>
                <div className="flex gap-4 mb-6 flex-wrap">
                  {months.map((month) => (
                    <label key={month} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={comparisonMonths.includes(month)}
                        onChange={(e) => {
                          if (e.target.checked && comparisonMonths.length < 3) {
                            setComparisonMonths([...comparisonMonths, month]);
                          } else if (!e.target.checked) {
                            setComparisonMonths(comparisonMonths.filter(m => m !== month));
                          }
                        }}
                        className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm text-slate-700">{month}</span>
                    </label>
                  ))}
                </div>
                <Button
                  onClick={loadComparisonData}
                  disabled={comparisonMonths.length < 2 || loading}
                >
                  {loading ? 'Loading...' : 'Load Comparison'}
                </Button>
                
                {comparisonMonths.length > 0 && comparisonMonths.length < 2 && (
                  <p className="text-sm text-yellow-600 mt-2">Select at least 2 months to compare</p>
                )}
              </div>
            </div>

            {/* Comparison Results */}
            {comparisonReports.length >= 2 && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Bills Comparison */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900">Total Bills</h3>
                    </div>
                    <div className="p-4">
                      {comparisonReports.map((cr, idx) => {
                        const total = cr.report.totals?.totalLatestMonthBills || 0;
                        const prevTotal = comparisonReports[idx + 1]?.report.totals?.totalLatestMonthBills || 0;
                        const delta = idx < comparisonReports.length - 1 ? total - prevTotal : null;
                        const deltaPercent = prevTotal > 0 && delta !== null ? ((delta / prevTotal) * 100).toFixed(1) : null;
                        
                        return (
                          <div key={cr.month} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-600">{cr.month}</span>
                            <div className="text-right">
                              <span className="text-lg font-semibold text-slate-900">{formatCurrency(total)}</span>
                              {delta !== null && (
                                <span className={`ml-2 text-xs ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-green-600' : 'text-slate-400'}`}>
                                  {delta > 0 ? '+' : ''}{formatCurrency(delta)} ({deltaPercent}%)
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lines Count Comparison */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900">Active Services</h3>
                    </div>
                    <div className="p-4">
                      {comparisonReports.map((cr, idx) => {
                        const lines = cr.report.rows?.length || 0;
                        const prevLines = comparisonReports[idx + 1]?.report.rows?.length || 0;
                        const delta = idx < comparisonReports.length - 1 ? lines - prevLines : null;
                        
                        return (
                          <div key={cr.month} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-600">{cr.month}</span>
                            <div className="text-right">
                              <span className="text-lg font-semibold text-slate-900">{lines}</span>
                              {delta !== null && delta !== 0 && (
                                <span className={`ml-2 text-xs ${delta > 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                  {delta > 0 ? '+' : ''}{delta}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Data Usage Comparison */}
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-200">
                      <h3 className="font-semibold text-slate-900">Data Usage</h3>
                    </div>
                    <div className="p-4">
                      {comparisonReports.map((cr, idx) => {
                        const data = (cr.report.totals?.totalLatestMonthDataMb || 0) / 1024; // Convert to GB
                        const prevData = ((comparisonReports[idx + 1]?.report.totals?.totalLatestMonthDataMb || 0)) / 1024;
                        const delta = idx < comparisonReports.length - 1 ? data - prevData : null;
                        const deltaPercent = prevData > 0 && delta !== null ? ((delta / prevData) * 100).toFixed(1) : null;
                        
                        return (
                          <div key={cr.month} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                            <span className="text-sm text-slate-600">{cr.month}</span>
                            <div className="text-right">
                              <span className="text-lg font-semibold text-slate-900">{data.toFixed(1)} GB</span>
                              {delta !== null && (
                                <span className={`ml-2 text-xs ${delta > 0 ? 'text-blue-600' : delta < 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                                  {delta > 0 ? '+' : ''}{delta.toFixed(1)}GB ({deltaPercent}%)
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Changes Detail */}
                {comparisonReports.length === 2 && (() => {
                  const [newer, older] = comparisonReports;
                  const newerNumbers = new Set(newer.report.rows?.map(r => r.mobileNumber) || []);
                  const olderNumbers = new Set(older.report.rows?.map(r => r.mobileNumber) || []);
                  
                  const added = newer.report.rows?.filter(r => !olderNumbers.has(r.mobileNumber)) || [];
                  const removed = older.report.rows?.filter(r => !newerNumbers.has(r.mobileNumber)) || [];
                  
                  // Find cost changes for common lines
                  const costChanges: { number: string; user: string; oldCost: number; newCost: number; delta: number }[] = [];
                  newer.report.rows?.forEach(nr => {
                    const or = older.report.rows?.find(o => o.mobileNumber === nr.mobileNumber);
                    if (or) {
                      const delta = (nr.latestMonthBillTotal || 0) - (or.latestMonthBillTotal || 0);
                      if (Math.abs(delta) > 5) { // Only show significant changes (> $5)
                        costChanges.push({
                          number: nr.mobileNumber,
                          user: nr.userName || '',
                          oldCost: or.latestMonthBillTotal || 0,
                          newCost: nr.latestMonthBillTotal || 0,
                          delta
                        });
                      }
                    }
                  });
                  costChanges.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Lines Added/Removed */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                          <h3 className="font-semibold text-slate-900">Line Changes</h3>
                        </div>
                        <div className="p-4 max-h-80 overflow-y-auto">
                          {added.length === 0 && removed.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No lines added or removed</p>
                          ) : (
                            <>
                              {added.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-sm font-medium text-green-700 mb-2">➕ Added ({added.length})</h4>
                                  {added.slice(0, 10).map(r => (
                                    <div key={r.mobileNumber} className="flex justify-between text-sm py-1 border-b border-slate-50">
                                      <span className="text-slate-700">{r.mobileNumber}</span>
                                      <span className="text-slate-500">{r.userName || '-'}</span>
                                    </div>
                                  ))}
                                  {added.length > 10 && (
                                    <p className="text-xs text-slate-400 mt-1">...and {added.length - 10} more</p>
                                  )}
                                </div>
                              )}
                              {removed.length > 0 && (
                                <div>
                                  <h4 className="text-sm font-medium text-red-700 mb-2">➖ Removed ({removed.length})</h4>
                                  {removed.slice(0, 10).map(r => (
                                    <div key={r.mobileNumber} className="flex justify-between text-sm py-1 border-b border-slate-50">
                                      <span className="text-slate-700">{r.mobileNumber}</span>
                                      <span className="text-slate-500">{r.userName || '-'}</span>
                                    </div>
                                  ))}
                                  {removed.length > 10 && (
                                    <p className="text-xs text-slate-400 mt-1">...and {removed.length - 10} more</p>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Cost Changes */}
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                          <h3 className="font-semibold text-slate-900">Significant Cost Changes (&gt;$5)</h3>
                        </div>
                        <div className="p-4 max-h-80 overflow-y-auto">
                          {costChanges.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No significant cost changes</p>
                          ) : (
                            <>
                              {costChanges.slice(0, 15).map(c => (
                                <div key={c.number} className="flex justify-between items-center text-sm py-2 border-b border-slate-50">
                                  <div>
                                    <span className="text-slate-700">{c.number}</span>
                                    {c.user && <span className="text-slate-400 ml-2 text-xs">{c.user}</span>}
                                  </div>
                                  <div className="text-right">
                                    <span className="text-slate-400 text-xs">{formatCurrency(c.oldCost)} → </span>
                                    <span className="text-slate-700">{formatCurrency(c.newCost)}</span>
                                    <span className={`ml-2 font-medium ${c.delta > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      {c.delta > 0 ? '+' : ''}{formatCurrency(c.delta)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {costChanges.length > 15 && (
                                <p className="text-xs text-slate-400 mt-2">...and {costChanges.length - 15} more changes</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* No months available */}
            {months.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">No Data</h3>
                </div>
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Reports Available</h3>
                  <p className="text-sm text-slate-500">
                    Upload at least 2 reports to compare month-over-month changes.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {viewMode === 'analytics' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            {report && <QuickStats report={report} />}

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost by Device Type */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Cost by Device Type</h3>
                </div>
                <div className="p-4">
                  <DeviceTypePieChart report={report} />
                </div>
              </div>
              
              {/* Plan Distribution */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Plan Distribution</h3>
                </div>
                <div className="p-4">
                  <PlanDistributionChart report={report} />
                </div>
              </div>
              
              {/* Top Cost Lines */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Top 10 Lines by Cost</h3>
                </div>
                <div className="p-4">
                  <TopCostBarChart report={report} limit={10} />
                </div>
              </div>
              
              {/* Data Usage Leaders */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Top 10 Data Users</h3>
                </div>
                <div className="p-4">
                  <DataUsageChart report={report} limit={10} />
                </div>
              </div>
            </div>

            {/* Optimization Tips */}
            {report && report.rows && report.rows.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Optimization Insights</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Zero-bill lines */}
                    {(() => {
                      const zeroBillLines = report.rows.filter(r => (r.latestMonthBillTotal || 0) === 0);
                      if (zeroBillLines.length === 0) return null;
                      return (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">⚠️</span>
                            <div>
                              <h4 className="font-medium text-yellow-800">Zero-Bill Lines</h4>
                              <p className="text-sm text-yellow-700 mt-1">
                                {zeroBillLines.length} line{zeroBillLines.length !== 1 ? 's' : ''} with $0 bill. 
                                Review for inactive/unused services.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Low data usage */}
                    {(() => {
                      const lowDataLines = report.rows.filter(r => {
                        const data = r.latestMonthDataMb || 0;
                        const allowance = r.includedData || 0;
                        return allowance > 0 && data < allowance * 0.2; // Using less than 20%
                      });
                      if (lowDataLines.length < 3) return null;
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">📉</span>
                            <div>
                              <h4 className="font-medium text-blue-800">Underutilized Plans</h4>
                              <p className="text-sm text-blue-700 mt-1">
                                {lowDataLines.length} lines using &lt;20% of data allowance. 
                                Consider downgrading plans.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* High data usage */}
                    {(() => {
                      const highDataLines = report.rows.filter(r => {
                        const data = r.latestMonthDataMb || 0;
                        const allowance = r.includedData || 0;
                        return allowance > 0 && data > allowance * 0.9; // Using more than 90%
                      });
                      if (highDataLines.length === 0) return null;
                      return (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">📈</span>
                            <div>
                              <h4 className="font-medium text-red-800">Near Limit</h4>
                              <p className="text-sm text-red-700 mt-1">
                                {highDataLines.length} line{highDataLines.length !== 1 ? 's' : ''} using &gt;90% of allowance. 
                                Consider upgrading to avoid overages.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* No data state */}
            {(!report || !report.rows || report.rows.length === 0) && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Analytics</h3>
                </div>
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Award className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Report Data</h3>
                  <p className="text-sm text-slate-500">
                    Upload a Telstra mobile report to view analytics and insights.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {viewMode === 'invoices' && invoiceAccess && selectedCustomer && (
          <TelstraInvoicesPanel
            customerId={selectedCustomer.customerId}
            customerName={selectedCustomer.customerName || selectedCustomer.customerId}
          />
        )}

      </div>

      <TelstraBlitzValidationModal
        open={showConfirmModal && !!mobilePreview}
        targetCustomerName={selectedCustomer?.customerName || selectedCustomer?.customerId || 'Unknown'}
        parsedReport={mobilePreview?.report || null}
        warnings={mobilePreview?.warnings}
        saving={uploading}
        onConfirm={handleApproveAndSave}
        onCancel={() => setShowConfirmModal(false)}
      />

      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed bottom-5 right-5 px-4 py-3 rounded-xl shadow-lg z-50 text-white text-sm font-medium flex items-center gap-2 ${
          toast.type === 'success' ? 'bg-green-600' : 
          toast.type === 'warning' ? 'bg-yellow-600' : 
          toast.type === 'error' ? 'bg-red-600' : 
          'bg-blue-600'
        }`}>
          {toast.type === 'success' && <Check className="h-4 w-4" />}
          {toast.type === 'error' && <X className="h-4 w-4" />}
          {toast.message}
        </div>
      )}
    </div>
  );
}
